// src/pinball/ScoringSystem.ts - Scoring with multipliers, combos, jackpots, end-of-ball bonus

import {
  ScoringConfig,
  TableElementConfig,
  PinballGameState,
  CollisionEvent,
} from './types';

export interface ScoreEvent {
  points: number;
  multipliedPoints: number;
  element: string;
  isCombo: boolean;
  comboLevel: number;
  isJackpot: boolean;
}

export class ScoringSystem {
  private config: ScoringConfig;
  private comboCount: number;
  private lastHitTimestamp: number;
  private scoreMultiplier: number;
  private bonusMultiplier: number;
  private elementsHitThisBall: Set<string>;
  private rampsCompletedThisBall: number;
  private lanesCompletedThisBall: number;
  private targetsHitThisBall: number;

  constructor(config: ScoringConfig) {
    this.config = config;
    this.comboCount = 0;
    this.lastHitTimestamp = 0;
    this.scoreMultiplier = 1;
    this.bonusMultiplier = 1;
    this.elementsHitThisBall = new Set();
    this.rampsCompletedThisBall = 0;
    this.lanesCompletedThisBall = 0;
    this.targetsHitThisBall = 0;
  }

  processHit(element: TableElementConfig, state: PinballGameState): ScoreEvent {
    const now = Date.now();
    const basePoints = element.properties.pointValue;

    // Update combo
    this.updateCombo(now);

    // Track element hit
    this.elementsHitThisBall.add(element.id);

    // Track specific types
    if (element.type === 'ramp' || element.type === 'orbit') {
      this.rampsCompletedThisBall++;
    } else if (element.type === 'lane') {
      this.lanesCompletedThisBall++;
    } else if (element.type === 'target' || element.type === 'drop_target') {
      this.targetsHitThisBall++;
    }

    // Calculate combo multiplier
    const comboMultiplier = this.getComboMultiplier();
    const multipliedPoints = Math.round(basePoints * this.scoreMultiplier * comboMultiplier);

    const event: ScoreEvent = {
      points: basePoints,
      multipliedPoints,
      element: element.id,
      isCombo: this.comboCount > 0,
      comboLevel: this.comboCount,
      isJackpot: false,
    };

    this.lastHitTimestamp = now;
    this.comboCount++;

    return event;
  }

  updateCombo(timestamp: number): void {
    if (this.lastHitTimestamp > 0) {
      const elapsed = timestamp - this.lastHitTimestamp;
      if (elapsed > this.config.comboWindow) {
        this.comboCount = 0;
      }
    }
  }

  calculateEndOfBallBonus(state: PinballGameState): number {
    const baseBonus = this.config.endOfBallBonusBase;
    const laneBonus = this.lanesCompletedThisBall * 1000;
    const rampBonus = this.rampsCompletedThisBall * 2000;
    const targetBonus = this.targetsHitThisBall * 500;

    const totalBonus = (baseBonus + laneBonus + rampBonus + targetBonus) * this.bonusMultiplier;
    return Math.round(totalBonus);
  }

  awardJackpot(): ScoreEvent {
    const jackpotPoints = this.config.jackpotValue * this.scoreMultiplier;
    return {
      points: this.config.jackpotValue,
      multipliedPoints: Math.round(jackpotPoints),
      element: 'jackpot',
      isCombo: false,
      comboLevel: 0,
      isJackpot: true,
    };
  }

  increaseScoreMultiplier(amount: number = 1): void {
    this.scoreMultiplier += amount;
  }

  increaseBonusMultiplier(amount: number = 1): void {
    this.bonusMultiplier += amount;
  }

  getMultiplier(): number {
    return this.scoreMultiplier;
  }

  getBonusMultiplier(): number {
    return this.bonusMultiplier;
  }

  getComboCount(): number {
    return this.comboCount;
  }

  private getComboMultiplier(): number {
    if (this.comboCount <= 0) return 1;
    const index = Math.min(this.comboCount, this.config.comboMultipliers.length - 1);
    return this.config.comboMultipliers[index] || 1;
  }

  reset(): void {
    this.comboCount = 0;
    this.lastHitTimestamp = 0;
    this.scoreMultiplier = 1;
    this.bonusMultiplier = 1;
    this.elementsHitThisBall = new Set();
    this.rampsCompletedThisBall = 0;
    this.lanesCompletedThisBall = 0;
    this.targetsHitThisBall = 0;
  }

  resetForNewBall(): void {
    this.comboCount = 0;
    this.lastHitTimestamp = 0;
    this.bonusMultiplier = 1;
    this.elementsHitThisBall = new Set();
    this.rampsCompletedThisBall = 0;
    this.lanesCompletedThisBall = 0;
    this.targetsHitThisBall = 0;
    // scoreMultiplier persists across balls
  }
}
